import { Vector2, Vector3 } from 'three';

import noised from '@/libs/lygia/generative/noised.glsl';

export const uniforms = () => ({
    u_ray_density: { value: 1.0 },
    u_ray_intensity: { value: 0.0 },
    u_ray_speed: { value: 1.0 },
    u_resolution: { value: new Vector2() },
    u_transition_progress: { value: 0.0 },
    u_light_color: { value: new Vector3(0, 0, 1) },
    u_gamma_dist: { value: 2.2 },
    u_time: { value: 0.0 },
});

export const shaderChunks = {
    vertex: /* glsl */ `
        varying vec2 vUv;
        varying vec3 vPosition;
        varying vec3 vWorldPosition;
        
        void main() {
            vUv = uv;
            vPosition = position;
            vec4 worldPosition = modelMatrix * vec4(position, 1.0);
            vWorldPosition = worldPosition.xyz;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
    `,
    fragment: /* glsl */ `
        uniform float u_ray_density;
        uniform float u_ray_intensity;
        uniform float u_ray_speed;
        uniform vec2 u_resolution;
        uniform float u_transition_progress;
        uniform float u_time;
        uniform float u_gamma_dist;
        uniform vec3 u_light_color;

        varying vec2 vUv;
        varying vec3 vPosition;
        varying vec3 vWorldPosition;

        ${noised}

        // vec3 hsv2rgb(vec3 c) {
        //     vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
        //     vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
        //     return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
        // }
        
        // Hash function for pseudo-random numbers
        float hash21(vec2 p) {
            p = fract(p * vec2(234.34, 435.345));
            p += dot(p, p + 34.23);
            return fract(p.x * p.y);
        }

        // 2D Noise
        float noise(vec2 p) {
            vec2 i = floor(p);
            vec2 f = fract(p);
            
            float a = hash21(i);
            float b = hash21(i + vec2(1.0, 0.0));
            float c = hash21(i + vec2(0.0, 1.0));
            float d = hash21(i + vec2(1.0, 1.0));
            
            vec2 u = f * f * (3.0 - 2.0 * f);
            return mix(a, b, u.x) + (c - a)* u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
        }

        // FBM (Fractional Brownian Motion)
        float fbm(vec2 p, float frequency) {
            float value = 0.0;
            float amplitude = 1.0;
            // float frequency = 0.7;
            float total = amplitude;
            for(int i = 0; i < 4; i++) {
                value += amplitude * noise(p * frequency);
                frequency *= 2.0;
                amplitude *= 0.5;
                total += amplitude;
            }
            return value / total;
        }

        // Function to create a radial ray pattern
        float rays(vec2 uv, vec2 lightPos, float count, float time, float freq) {
            vec2 toLight = uv - lightPos;
            float angle = atan(toLight.y, toLight.x);
            float dist = length(toLight);
            
            // Create periodic noise coordinates
            float noiseScale = 5.0;
            vec2 noiseCoord = vec2(
                cos(angle) * noiseScale + time * 0.2,
                sin(angle) * noiseScale + time * 0.1
            );
            float variation = pow(fbm(noiseCoord, freq), 2.0) * 2.0;
            
            // Combine noise with base ray pattern
            float rayLength = 0.75 + variation * 0.25;
            float rays = abs(sin(angle * count + -time * 1.0 + variation * 5.0));
            float falloff =pow(1.0 - abs(0.5 - uv.x)*2.0, 3.0);//1.0 - abs(0.5 - uv.x)*2.0;
            falloff *= pow(1.0 - uv.y, 3.0);
            // float falloff = 1.0;//max(0.0, 1.0 - dist/0.5);// - dist / 0.25 ;//pow(1.0 - smoothstep(0.0, 0.5 - (1.0 - rayLength) * 0.5, dist), 1.0)*1.0;
            return variation * falloff;// * (0.8 + rays * 0.2);
        }
        vec3 hsv2rgb(vec3 c) {
            vec4 K = vec4(1.0, 2.0/3.0, 1.0/3.0, 3.0);
            vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
            return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
        }
        
        void main() {
            vec2 uv = vUv;
            // Scale UV based on width and height controls
            uv = (uv - 0.5) * 1.0  + 0.5;
            float rayCount = 12.0;
            // Create animated rays
            vec2 origin = vec2(0.5,-.15);
            float ray = rays(uv, origin, rayCount, u_time*1.0, 0.5);
            float ray2 = rays(uv, origin, rayCount, u_time*2.0, 0.4);
            
            ray *= pow(ray2, 2.0);
            // Create a soft glow around the light source
            float glow = 1.0 - smoothstep(0.0, 0.5, length(vUv - origin));
            
            // Combine rays and glow
            float shine = ray * u_ray_intensity ;
            vec2 toOrigin = uv - origin;
            float angle = (atan(toOrigin.y, toOrigin.x) + 1.0 ) * .5;
            shine *=min(1.0, max(0.0,u_transition_progress*3.0- uv.x))*10.0;// 1.0 - max(0.0, u_transition_progress*2.0 - angle);
            // shine = min(1.0, pow(finalColor, 2.2));
            

            // Create warm light color
            // vec3 lightColor = vec3(1.0, 0.9, 0.7);
            // vec3 lightColor = vec3(1.0, 0.0, 1.0);
            vec3 lightColor = u_light_color;//
            float dist = length(uv - origin);
            lightColor = pow(lightColor, 1.0 + dist * vec3(u_gamma_dist));
            // lightColor += hsv2rgb(vec3(fract(vUv.y*0.5-u_time*0.3), 0.5, 1.0));
            
            float distMod = .5 + pow(dist, 2.0);
            float bottomPower = pow(1.0 - vUv.y, 6.0) * 1.0;
            vec3 finalColor = lightColor * shine * (u_ray_intensity+bottomPower);


            // vec3 finalColor = vec3(0.0, 0.0, 0.0) + lightColor* shine;
            finalColor *= vec3(smoothstep(0.0, 0.1, vUv.y)) *1.0;

            float alphaOut = smoothstep(0.7, 0.6, u_transition_progress);

            gl_FragColor = vec4(finalColor, alphaOut);
        }
    `,
};
